const SIZE_CLASS_BY_HEADER: Record<
  string,
  "text-large" | "text-medium" | "text-small" | "text-huge"
> = {
  H1: "text-large",
  H2: "text-medium",
  H3: "text-small",
};

const isAllowedTag = (tagName: string): boolean => {
  return ["P", "SPAN", "A", "B", "I", "U", "IMG", "BR", "HR"].includes(tagName);
};

const sanitizeElementTree = (root: HTMLElement) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  const toRemove: Element[] = [];

  // Collect first, mutate after (so the walker doesn't get confused)
  while (walker.nextNode()) {
    const el = walker.currentNode as Element;
    if (!isAllowedTag(el.tagName)) {
      toRemove.push(el);
      continue;
    }

    // Keep only safe attrs for allowed tags
    if (el.tagName === "A") {
      const href = el.getAttribute("href") || "";
      // Allow only http(s) and mailto
      if (
        href &&
        !/^https?:\/\//i.test(href) &&
        !/^mailto:/i.test(href) &&
        !/^\/\//.test(href)
      ) {
        el.removeAttribute("href");
      }
      // strip all attrs except href
      [...el.attributes].forEach((attr) => {
        if (attr.name !== "href") el.removeAttribute(attr.name);
      });
    } else if (el.tagName === "IMG") {
      const src = el.getAttribute("src") || "";
      // keep only src + alt
      [...el.attributes].forEach((attr) => {
        if (attr.name !== "src" && attr.name !== "alt") {
          el.removeAttribute(attr.name);
        }
      });
      // If src is empty, drop image
      if (!src) toRemove.push(el);
    } else if (el.tagName === "SPAN") {
      // Allow size classes + optional heading marker
      const cls = (el.getAttribute("class") || "").trim();
      const allowed = /^(text-(small|medium|large|huge))(?:\s+heading)?$/;
      if (cls && !allowed.test(cls)) {
        el.removeAttribute("class");
      }
      // remove other attrs
      [...el.attributes].forEach((attr) => {
        if (attr.name !== "class") el.removeAttribute(attr.name);
      });
    } else {
      // For other tags, remove all attributes
      [...el.attributes].forEach((attr) => el.removeAttribute(attr.name));
    }
  }

  // Remove by unwrapping content (preserve text)
  toRemove.forEach((el) => {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  });
};

export const normalizeArticleHtml = (html: string): string => {
  if (!html) return "";
  const container = document.createElement("div");
  container.innerHTML = html;

  // Convert headers to <p><span class="text-..."><b>...</b></span></p>
  (["h1", "h2", "h3"] as const).forEach((tag) => {
    const nodes = Array.from(container.querySelectorAll(tag));
    nodes.forEach((node) => {
      const sizeClass = SIZE_CLASS_BY_HEADER[node.tagName] || "text-medium";
      const p = document.createElement("p");
      const span = document.createElement("span");
      span.setAttribute("class", `${sizeClass} heading`);
      const b = document.createElement("b");
      b.innerHTML = node.innerHTML;
      span.appendChild(b);
      p.appendChild(span);
      node.replaceWith(p);
    });
  });

  // Convert blockquote to <p><span class="text-medium"><b><i>...</i></b></span></p>
  Array.from(container.querySelectorAll("blockquote")).forEach((node) => {
    const p = document.createElement("p");
    const span = document.createElement("span");
    span.setAttribute("class", "text-medium");
    const b = document.createElement("b");
    const i = document.createElement("i");
    i.innerHTML = node.innerHTML;
    b.appendChild(i);
    span.appendChild(b);
    p.appendChild(span);
    node.replaceWith(p);
  });

  // Ensure Quill's size classes become our classes (if any slipped in)
  Array.from(container.querySelectorAll("span")).forEach((span) => {
    const cls = span.getAttribute("class") || "";
    // Typical Quill class: ql-size-small/large/huge
    const m = cls.match(/ql-size-(small|large|huge)/);
    if (m) {
      const mapped =
        m[1] === "small"
          ? "text-small"
          : m[1] === "large"
            ? "text-medium"
            : m[1] === "huge"
              ? "text-huge"
              : "text-large";
      span.setAttribute("class", mapped);
    }
  });

  sanitizeElementTree(container);

  // Preserve "blank lines" as <p>&nbsp;</p> (typical articles spacing)
  Array.from(container.querySelectorAll("p")).forEach((p) => {
    const text = (p.textContent || "").replace(/\s+/g, "").trim();
    const hasImg = p.querySelector("img");
    const hasHr = p.querySelector("hr");
    const hasBr = p.querySelector("br");
    if (!text && !hasImg && !hasHr) {
      // Keep paragraph spacing instead of removing it
      p.innerHTML = hasBr ? "&nbsp;" : "&nbsp;";
    }
  });

  return container.innerHTML;
};
